import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * AdminReplenishment — Low-stock + reorder suggestion dashboard.
 * Uses RPC compute_reorder_suggestions(days, cover_days, only_low, limit).
 */
export function AdminReplenishment({ lang, toast }: { lang: string; toast?: (m: string) => void }) {
  const es = lang === "es";
  const [rows, setRows] = useState<any[]>([]);
  const [days, setDays] = useState(30);
  const [cover, setCover] = useState(30);
  const [onlyLow, setOnlyLow] = useState(true);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await (supabase as any).rpc("compute_reorder_suggestions", {
      _days: days, _cover_days: cover, _only_low: onlyLow, _limit: 200,
    });
    setLoading(false);
    if (error) { toast?.(error.message); return; }
    setRows(data || []);
  }, [days, cover, onlyLow, toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r: any) =>
      (r.name || "").toLowerCase().includes(s) ||
      (r.brand || "").toLowerCase().includes(s) ||
      (r.sku || "").toLowerCase().includes(s));
  }, [rows, search]);

  const totals = useMemo(() => {
    const out = filtered.filter((r: any) => (r.stock_quantity ?? 0) === 0).length;
    const low = filtered.filter((r: any) => (r.stock_quantity ?? 0) > 0 && (r.stock_quantity ?? 0) <= (r.low_stock_threshold ?? 10)).length;
    const units = filtered.reduce((a: number, r: any) => a + Number(r.suggested_reorder || 0), 0);
    return { out, low, units };
  }, [filtered]);

  const exportCSV = () => {
    const head = ["SKU","Producto","Marca","Stock","Umbral","Vendidos","Velocidad/día","Días cobertura","Sugerido reponer"];
    const lines = [head.join(",")].concat(filtered.map((r: any) => [
      r.sku || "", JSON.stringify(r.name || ""), r.brand || "",
      r.stock_quantity ?? 0, r.low_stock_threshold ?? 10,
      r.units_sold ?? 0, r.daily_velocity ?? 0,
      r.days_of_cover ?? "", r.suggested_reorder ?? 0,
    ].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `reposicion-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const runAlertNow = async () => {
    setRunning(true);
    try {
      const { error } = await supabase.functions.invoke("low-stock-alert", { body: {} });
      if (error) throw error;
      toast?.(es ? "Alerta enviada al admin" : "Alert sent to admin");
    } catch (e: any) {
      toast?.(e?.message || "Error");
    } finally { setRunning(false); }
  };

  const applyReorder = async (productId: string, qty: number) => {
    if (!qty || qty <= 0) return;
    const { data: p } = await supabase.from("products").select("stock_quantity").eq("id", productId).maybeSingle();
    const cur = Number(p?.stock_quantity ?? 0);
    const { error } = await supabase.from("products").update({ stock_quantity: cur + qty }).eq("id", productId);
    if (error) { toast?.(error.message); return; }
    toast?.(es ? `+${qty} agregadas al stock` : `+${qty} added to stock`);
    load();
  };

  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0 }}>{es ? "Reposición sugerida" : "Suggested replenishment"}</h3>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className="btn" onClick={exportCSV}>{es ? "Exportar CSV" : "Export CSV"}</button>
          <button className="btn btn-primary" onClick={runAlertNow} disabled={running}>
            {running ? "…" : (es ? "Enviar alerta ahora" : "Send alert now")}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8, margin: "12px 0" }}>
        <label style={{ fontSize: 12 }}>{es ? "Ventana de ventas (días)" : "Sales window (days)"}
          <input type="number" className="input" value={days} min={1} onChange={e => setDays(Math.max(1, Number(e.target.value) || 30))} />
        </label>
        <label style={{ fontSize: 12 }}>{es ? "Cobertura objetivo (días)" : "Target cover (days)"}
          <input type="number" className="input" value={cover} min={1} onChange={e => setCover(Math.max(1, Number(e.target.value) || 30))} />
        </label>
        <label style={{ fontSize: 12 }}>{es ? "Buscar" : "Search"}
          <input className="input" value={search} onChange={e => setSearch(e.target.value)} placeholder={es ? "nombre / SKU / marca" : "name / SKU / brand"} />
        </label>
        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, marginTop: 18 }}>
          <input type="checkbox" checked={onlyLow} onChange={e => setOnlyLow(e.target.checked)} />
          {es ? "Solo bajo umbral o sin cobertura" : "Only low-stock or under-cover"}
        </label>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12, color: "var(--mu)", marginBottom: 8 }}>
        <span>{es ? "Sin stock" : "Out of stock"}: <b style={{ color: "var(--am)" }}>{totals.out}</b></span>
        <span>{es ? "Bajo umbral" : "Low stock"}: <b style={{ color: "var(--am)" }}>{totals.low}</b></span>
        <span>{es ? "Unidades sugeridas" : "Suggested units"}: <b>{totals.units}</b></span>
        <span>{es ? "Productos analizados" : "Products"}: <b>{filtered.length}</b></span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>SKU</th><th>{es ? "Producto" : "Product"}</th>
              <th>Stock</th><th>{es ? "Umbral" : "Threshold"}</th>
              <th>{es ? "Vendidos" : "Sold"}</th>
              <th>{es ? "Vel/día" : "Vel/day"}</th>
              <th>{es ? "Cobertura" : "Cover"}</th>
              <th>{es ? "Sugerido" : "Suggested"}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{ textAlign: "center", padding: 16 }}>…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={9} style={{ textAlign: "center", padding: 16, color: "var(--mu)" }}>
                {es ? "Sin productos por reponer" : "No products to replenish"}
              </td></tr>
            )}
            {filtered.map((r: any) => {
              const stock = Number(r.stock_quantity ?? 0);
              const thr = Number(r.low_stock_threshold ?? 10);
              const color = stock === 0 ? "var(--am)" : stock <= thr ? "var(--am)" : "var(--ac)";
              return (
                <tr key={r.product_id}>
                  <td style={{ fontSize: 11, color: "var(--mu)" }}>{r.sku || "—"}</td>
                  <td>
                    <div>{r.name}</div>
                    {r.brand && <div style={{ fontSize: 11, color: "var(--mu)" }}>{r.brand}</div>}
                  </td>
                  <td style={{ color, fontWeight: 700 }}>{stock}</td>
                  <td>{thr}</td>
                  <td>{r.units_sold ?? 0}</td>
                  <td>{Number(r.daily_velocity ?? 0).toFixed(2)}</td>
                  <td>{r.days_of_cover == null ? "—" : Number(r.days_of_cover).toFixed(1)}</td>
                  <td style={{ fontWeight: 700 }}>{r.suggested_reorder ?? 0}</td>
                  <td>
                    <button className="btn" disabled={!r.suggested_reorder} onClick={() => applyReorder(r.product_id, Number(r.suggested_reorder || 0))}>
                      {es ? "Reponer" : "Restock"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}